import { useEffect, useMemo, useState } from 'react';
import SelectField from '../common/SelectField.js';
import DestinationHubMultiSelect from '../location-hub/DestinationHubMultiSelect.js';
import { formatLocationDropdownLabel, sortLocationsByNumber } from '../../utils/locationDropdownLabel.js';
import companySettingsService from '../../services/companySettingsService.js';
import { isLrShipmentFormat } from '../../utils/shipmentIdFormat.js';
import Button from '../common/Button.js';
import { ChevronUp, ChevronDown, X, Plus } from 'lucide-react';

const fieldClass =
  'w-full min-w-0 px-3 sm:px-4 py-2.5 sm:py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm bg-white';

export const emptyRouteForm = () => ({
  service_number: '',
  name: '',
  source_location_ids: [],
  target_location_ids: [],
  via_location_ids: [],
  departure_time: '',
  arrival_time: '',
  crossing_points: [],
});

const idsFromLocations = (locations, fallbackIds) => {
  const fromRows = (locations || []).map((hub) => hub?.id).filter((id) => id != null);
  if (fromRows.length) return fromRows.map(String);
  return (fallbackIds || []).map(String);
};

export const routeToForm = (route) => ({
  service_number: route?.service_number || route?.route_id || '',
  name: route?.name || '',
  source_location_ids: idsFromLocations(route?.source_locations, route?.source_location_ids),
  target_location_ids: idsFromLocations(route?.target_locations, route?.target_location_ids),
  via_location_ids: idsFromLocations(route?.via_locations, route?.via_location_ids).map(Number),
  departure_time: route?.departure_time || '',
  arrival_time: route?.arrival_time || '',
  crossing_points: (route?.crossing_points || []).map((point) => ({
    location_hub_id: String(point.location_hub_id || ''),
    branch_ids: (point.branch_ids || []).map(String),
  })),
});

const hubLabel = (hub, useNumberCode) =>
  hub?.route_label || formatLocationDropdownLabel(hub, useNumberCode);

const RouteForm = ({
  hubs,
  lookupHubs = [],
  formData,
  setFormData,
  onSubmit,
  onCancel,
  loading,
  submitLabel,
}) => {
  const [viaPick, setViaPick] = useState('');
  const [crossingPick, setCrossingPick] = useState('');
  const [useNumberCode, setUseNumberCode] = useState(false);

  useEffect(() => {
    let cancelled = false;
    companySettingsService.getSettings()
      .then((res) => {
        if (cancelled) return;
        const lrOn = (
          !!res?.data?.lr_branch_code_required
          || isLrShipmentFormat(res?.data?.shipment_id_format)
        );
        setUseNumberCode(lrOn);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const orderedHubs = useMemo(() => sortLocationsByNumber(hubs), [hubs]);

  const mergedLookupHubs = useMemo(() => {
    const seen = new Set();
    const rows = [];
    [...orderedHubs, ...(lookupHubs || [])].forEach((hub) => {
      const key = hub?.id != null ? String(hub.id) : '';
      if (!key || seen.has(key)) return;
      seen.add(key);
      rows.push(hub);
    });
    return rows;
  }, [orderedHubs, lookupHubs]);

  const hubById = useMemo(() => {
    const map = {};
    mergedLookupHubs.forEach((hub) => {
      map[String(hub.id)] = hub;
    });
    return map;
  }, [mergedLookupHubs]);

  const routeLocationIds = useMemo(() => {
    const ids = [
      ...formData.source_location_ids,
      ...formData.via_location_ids.map(String),
      ...formData.target_location_ids,
    ];
    return [...new Set(ids.filter(Boolean))];
  }, [formData.source_location_ids, formData.via_location_ids, formData.target_location_ids]);

  const availableViaHubs = orderedHubs.filter((hub) => {
    const id = String(hub.id);
    if (formData.source_location_ids.includes(id)) return false;
    if (formData.target_location_ids.includes(id)) return false;
    if (formData.via_location_ids.map(String).includes(id)) return false;
    return true;
  });

  const usedCrossing = new Set(formData.crossing_points.map((point) => String(point.location_hub_id)));
  const crossingChoices = sortLocationsByNumber(
    routeLocationIds
      .filter((id) => !usedCrossing.has(String(id)))
      .map((id) => hubById[String(id)])
      .filter(Boolean),
  ).map((hub) => String(hub.id));

  const keepCrossings = (points, sources, vias, targets) => {
    const onRoute = new Set([
      ...sources.map(String),
      ...vias.map(String),
      ...targets.map(String),
    ]);
    return points.filter((point) => onRoute.has(String(point.location_hub_id)));
  };

  const addViaStop = () => {
    if (!viaPick) return;
    const id = Number(viaPick);
    setFormData((prev) => ({
      ...prev,
      via_location_ids: [...prev.via_location_ids, id],
    }));
    setViaPick('');
  };

  const removeVia = (id) => {
    setFormData((prev) => ({
      ...prev,
      via_location_ids: prev.via_location_ids.filter((item) => item !== id),
      crossing_points: prev.crossing_points.filter((point) => String(point.location_hub_id) !== String(id)),
    }));
  };

  const moveVia = (index, direction) => {
    setFormData((prev) => {
      const next = [...prev.via_location_ids];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return { ...prev, via_location_ids: next };
    });
  };

  const onSources = (ids) => {
    setFormData((prev) => {
      const vias = prev.via_location_ids.filter((item) => !ids.includes(String(item)));
      const targets = prev.target_location_ids.filter((item) => !ids.includes(String(item)));
      return {
        ...prev,
        source_location_ids: ids,
        via_location_ids: vias,
        target_location_ids: targets,
        crossing_points: keepCrossings(prev.crossing_points, ids, vias, targets),
      };
    });
  };

  const onTargets = (ids) => {
    setFormData((prev) => {
      const sources = prev.source_location_ids.filter((item) => !ids.includes(String(item)));
      const vias = prev.via_location_ids.filter((item) => !ids.includes(String(item)));
      return {
        ...prev,
        source_location_ids: sources,
        target_location_ids: ids,
        via_location_ids: vias,
        crossing_points: keepCrossings(prev.crossing_points, sources, vias, ids),
      };
    });
  };

  const addCrossing = () => {
    if (!crossingPick) return;
    setFormData((prev) => ({
      ...prev,
      crossing_points: [
        ...prev.crossing_points,
        { location_hub_id: String(crossingPick), branch_ids: [] },
      ],
    }));
    setCrossingPick('');
  };

  const updateCrossingBranches = (locationId, branchIds) => {
    setFormData((prev) => ({
      ...prev,
      crossing_points: prev.crossing_points.map((point) => (
        String(point.location_hub_id) === String(locationId)
          ? { ...point, branch_ids: branchIds }
          : point
      )),
    }));
  };

  const removeCrossing = (locationId) => {
    setFormData((prev) => ({
      ...prev,
      crossing_points: prev.crossing_points.filter(
        (point) => String(point.location_hub_id) !== String(locationId)
      ),
    }));
  };

  const branchesFor = (locationId) => {
    const taken = new Set();
    formData.crossing_points.forEach((point) => {
      if (String(point.location_hub_id) === String(locationId)) return;
      (point.branch_ids || []).forEach((id) => taken.add(String(id)));
    });
    return orderedHubs.filter((hub) => String(hub.id) !== String(locationId) && !taken.has(String(hub.id)));
  };

  const pathPreview = () => {
    const names = (ids) => ids
      .map((id) => hubById[String(id)]?.location)
      .filter(Boolean);
    const chunks = [];
    const sources = names(formData.source_location_ids);
    const vias = names(formData.via_location_ids);
    const targets = names(formData.target_location_ids);
    if (sources.length) chunks.push(sources.join(', '));
    if (vias.length) chunks.push(vias.join(' → '));
    if (targets.length) chunks.push(targets.join(', '));
    return chunks.length ? chunks.join(' → ') : 'Select source and target locations';
  };

  return (
    <form onSubmit={onSubmit} className="bg-white rounded-xl shadow-sm p-4 sm:p-8 space-y-6 sm:space-y-8">
      <div>
        <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
          Route Information
        </h3>
        <p className="text-xs sm:text-sm text-gray-600 leading-relaxed mb-4 sm:mb-6">
          The service number is the route id. Source and target locations are required.
          Departure, arrival, and crossing locations are optional.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
          <div className="min-w-0">
            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
              Service Number <span className="text-danger font-bold">*</span>
            </label>
            <input
              type="text"
              value={formData.service_number}
              onChange={(e) => setFormData((prev) => ({ ...prev, service_number: e.target.value.toUpperCase() }))}
              placeholder="SRV-1001"
              className={fieldClass}
              required
              maxLength={40}
            />
            <p className="text-[11px] text-gray-500 mt-1">This is the route id used on the dispatch Excel.</p>
          </div>
          <div className="min-w-0">
            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
              Route Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Optional — e.g. Mumbai to Delhi"
              className={fieldClass}
            />
          </div>
          <div className="min-w-0">
            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
              Source Locations <span className="text-danger font-bold">*</span>
            </label>
            <DestinationHubMultiSelect
              hubs={orderedHubs.filter((hub) => !formData.target_location_ids.includes(String(hub.id)))}
              lookupHubs={lookupHubs}
              value={formData.source_location_ids}
              onChange={onSources}
              placeholder="Select source locations"
              selectedNoun="source location"
              useNumberCode={useNumberCode}
            />
          </div>
          <div className="min-w-0">
            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
              Target Locations <span className="text-danger font-bold">*</span>
            </label>
            <DestinationHubMultiSelect
              hubs={orderedHubs.filter((hub) => !formData.source_location_ids.includes(String(hub.id)))}
              lookupHubs={lookupHubs}
              value={formData.target_location_ids}
              onChange={onTargets}
              placeholder="Select target locations"
              selectedNoun="target location"
              useNumberCode={useNumberCode}
            />
          </div>
          <div className="min-w-0">
            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
              Departure Time
            </label>
            <input
              type="time"
              value={formData.departure_time}
              onChange={(e) => setFormData((prev) => ({ ...prev, departure_time: e.target.value }))}
              className={fieldClass}
            />
          </div>
          <div className="min-w-0">
            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
              Arrival Time
            </label>
            <input
              type="time"
              value={formData.arrival_time}
              onChange={(e) => setFormData((prev) => ({ ...prev, arrival_time: e.target.value }))}
              className={fieldClass}
            />
          </div>
        </div>

        <div className="min-w-0 mt-4 sm:mt-6">
          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
            Via Stops (travel order)
          </label>
          <div className="flex flex-row items-stretch gap-2 mb-2 sm:mb-3">
            <SelectField
              variant="form"
              value={viaPick}
              onChange={(e) => setViaPick(e.target.value)}
              className={`${fieldClass} flex-1`}
            >
              <option value="">Add middle stop…</option>
              {availableViaHubs.map((hub) => (
                <option key={hub.id} value={hub.id}>{hubLabel(hub, useNumberCode)}</option>
              ))}
            </SelectField>
            <Button type="button" variant="secondary" icon={Plus} mobileIconOnly mobileLabel="Add" onClick={addViaStop} disabled={!viaPick}>
              Add Stop
            </Button>
          </div>
          {formData.via_location_ids.length === 0 ? (
            <p className="text-[11px] sm:text-xs text-gray-500">No middle stops.</p>
          ) : (
            <ul className="space-y-1.5 sm:space-y-2">
              {formData.via_location_ids.map((id, index) => {
                const hub = hubById[String(id)];
                return (
                  <li key={`${id}-${index}`} className="flex items-center justify-between gap-2 px-2.5 sm:px-3 py-2 border border-gray-200 rounded-xl bg-gray-50 min-w-0">
                    <span className="text-xs sm:text-sm text-gray-800 truncate min-w-0">
                      {index + 1}. {hub ? hubLabel(hub, useNumberCode) : id}
                    </span>
                    <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
                      <button type="button" onClick={() => moveVia(index, -1)} className="p-1.5 rounded-lg hover:bg-white" title="Move up">
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button type="button" onClick={() => moveVia(index, 1)} className="p-1.5 rounded-lg hover:bg-white" title="Move down">
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <button type="button" onClick={() => removeVia(id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-600" title="Remove">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="min-w-0 mt-4 sm:mt-6">
          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1">
            Crossing Locations
          </label>
          <p className="text-[11px] sm:text-xs text-gray-500 mb-2">
            Optional. Pick a stop on this route, then choose the branches whose shipments
            that location’s agent can see in crossing.
          </p>
          <div className="flex flex-row items-stretch gap-2 mb-3">
            <SelectField
              variant="form"
              value={crossingPick}
              onChange={(e) => setCrossingPick(e.target.value)}
              className={`${fieldClass} flex-1`}
            >
              <option value="">Add crossing location…</option>
              {crossingChoices.map((id) => {
                const hub = hubById[id];
                return (
                  <option key={id} value={id}>{hub ? hubLabel(hub, useNumberCode) : id}</option>
                );
              })}
            </SelectField>
            <Button type="button" variant="secondary" icon={Plus} mobileIconOnly mobileLabel="Add" onClick={addCrossing} disabled={!crossingPick}>
              Add Crossing
            </Button>
          </div>
          {formData.crossing_points.length === 0 ? (
            <p className="text-[11px] sm:text-xs text-gray-500">No crossing locations.</p>
          ) : (
            <div className="space-y-3">
              {formData.crossing_points.map((point) => {
                const hub = hubById[String(point.location_hub_id)];
                return (
                  <div key={point.location_hub_id} className="border border-gray-200 rounded-xl p-3 bg-gray-50">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <p className="text-sm font-semibold text-gray-800">
                        {hub ? hubLabel(hub, useNumberCode) : point.location_hub_id}
                      </p>
                      <button type="button" onClick={() => removeCrossing(point.location_hub_id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-600" title="Remove crossing">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <DestinationHubMultiSelect
                      hubs={branchesFor(point.location_hub_id)}
                      value={point.branch_ids}
                      onChange={(ids) => updateCrossingBranches(point.location_hub_id, ids)}
                      placeholder="Branches that cross here"
                      selectedNoun="branch"
                      useNumberCode={useNumberCode}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="rounded-xl bg-orange-50 border border-orange-100 px-3 sm:px-4 py-2.5 sm:py-3 mt-4">
          <p className="text-[11px] sm:text-xs font-medium text-orange-800 mb-0.5 sm:mb-1">Travel path preview</p>
          <p className="text-xs sm:text-sm text-orange-900 font-semibold break-words">{pathPreview()}</p>
        </div>
      </div>

      <div className="flex flex-row items-center justify-end gap-2 sm:gap-3 pt-1 sm:pt-2">
        <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="primary" disabled={loading}>
          {loading ? 'Saving…' : submitLabel}
        </Button>
      </div>
    </form>
  );
};

export const toRoutePayload = (formData) => ({
  service_number: formData.service_number.trim(),
  name: formData.name.trim() || null,
  source_location_ids: formData.source_location_ids.map(Number),
  target_location_ids: formData.target_location_ids.map(Number),
  via_location_ids: formData.via_location_ids.map(Number),
  departure_time: formData.departure_time || null,
  arrival_time: formData.arrival_time || null,
  crossing_points: formData.crossing_points.map((point) => ({
    location_hub_id: Number(point.location_hub_id),
    branch_ids: (point.branch_ids || []).map(Number),
  })),
});

export default RouteForm;
